import {useCallback, useRef, useState} from "react";
import {del, insert, set} from 'object-path-immutable'
import {useHistory, useLocation} from 'react-router-dom';
import {
    closeScreenDesigner, generateUid,
    getScreenValues,
    onDrop, regenerateIds,
    removeScreenDesigner,
    saveScreenDesigner
} from "./utils/lib";
import {useSnackbar} from "notistack";
import findByString from "../../../../../Utils/Form/FormDynamic/Utils/getValueByString";
import _ from "lodash";
import {useEventListener} from "../../../../../Hooks/useEventListener";
import {insertSideBar, setSidebarState} from "./ScreenDesigner.slice";
import {useDispatch} from "react-redux";
import {getScriptFromBraces} from "../../../../JsonScreen/Utils/getSingleParamFromJs";

const SCREEN_DESIGNER_VERSION = 3;


export const screenValuesTemplate = {
    styles: [{
        width: "90%",
        height: '90%',
        justifyContent: "center",
        alignItems: "center"
    }],
    events: [{event: '', actions: [{param: [{}]}]}],
    entities: [],
    children: [],
    version: SCREEN_DESIGNER_VERSION
};

const useScreenDesigner = (inParamId) => {
    const {enqueueSnackbar} = useSnackbar();
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const [isDesignerReady, setIsDesignerReady] = useState(false);
    const [screenValues, setScreenValues] = useState(screenValuesTemplate);
    const [screenTypeProps, setScreenTypeProps] = useState({});
    const [resolution, setResolution] = useState({resolution: {width: '100%'}});
    const [openSelectTemplate, setOpenSelectTemplate] = useState(false);
    const [scriptSuggestions, setScriptSuggestions] = useState({entityList: [], allPaths: []})

    //undo redo state
    const [middleWareValues, setMiddleWareValues] = useState([]);
    const [currentStep, setCurrentStep] = useState(0);

    const [lists, setLists] = useState({});
    const [listStorage, setListStorage] = useState([]);
    const [openListDesigner, setOpenListDesigner] = useState({open: false, listId: ''});
    const [isChanged, setIsChanged] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadInitialData, setLoadInitialData] = useState(false);
    const [selectedComponent, setSelectedComponent] = useState(null);
    const selectedComponentRef = useRef(null);
    const [suggestions, setSuggestions] = useState({});
    const [uploadedImages, setUploadedImages] = useState({});
    const [preparedToSaveImages, setPreparedToSaveImages] = useState([]);
    const [preparedToSaveLists, setPreparedToSaveLists] = useState({});

    const eventListener = useCallback((e) => {
        if (e.ctrlKey && e.key === 'z') {
            undoAction();
        }
        if (e.ctrlKey && e.key === 'y') {
            redoAction();
        }
    }, [currentStep]);

    useEventListener('keydown', eventListener);
    //init screen designer
    const initData = useCallback(async (id) => {
        const screenType = (location.state && location.state.inParam) ? location.state.inParam.screenDesigner.type : 'screen';
        setLoadInitialData(true);
        const {listStorage, screenValues, screenTypeProps, scriptSuggestions: scriptSuggestionsG} = await getScreenValues(id, screenType);
        setScreenTypeProps(screenTypeProps)
        setScriptSuggestions(scriptSuggestionsG)
        //const availableLists = await getAvailableLists();
        setScreenValues(screenValues);
        setListStorage(listStorage);

        //check for sidebar and open
        const isExistsSidebar = screenValues.children.findIndex(item => item.type === 'sidebar') !== -1;
        dispatch(setSidebarState(Boolean(isExistsSidebar)))

        //set initial undo redo
        setMiddleWareValues([screenValues]);
        setCurrentStep(0);

        //set initial suggestions
        setSuggestions(suggestions => {
            return {
                ...suggestions,
                //lists: availableLists
            }
        });

        //initial resolution - desktop
        setResolution({resolution: {width: '100%', height: ''}});

        //disable initial loading
        setLoadInitialData(false);
        setIsDesignerReady(true)
    }, []);

    //screen designer actions
    const saveScreen = async (isPreSave) => {
        const response = await saveScreenDesigner({
            designerType: screenTypeProps.id,
            screenValues,
            preparedToSaveImages,
            preparedToSaveLists,
            history,
            location,
            setLoading,
            enqueueSnackbar,
            isPreSave,
            SCREEN_DESIGNER_VERSION,
            inParamId
        })

        if (isPreSave) {
            if (response.updatedScreen) {
                setScreenValues(response.updatedScreen)
            }
            if (response.updatedLists) {
                setPreparedToSaveLists(response.updatedLists)
            }
        }


    };
    const removeScreen = async () => {
        await removeScreenDesigner({
            enqueueSnackbar,
            id: screenValues.id,
            history,
            location,
            designerType: 'screen',
            setLoading
        })
    };
    const closeScreen = () => {
        closeScreenDesigner({location, history, isChanged})
    };
    const onSelectTemplate = (template) => {
        setScreenValues(prev => {
            return {
                ...prev,
                children: template.children,
                styles: template.styles,
                events: template.events,
            }

        })
    }
    const onSelectStyleTemplate = (template) => {
        const newSelected = set(selectedComponent, 'styles', template)
        setSelectedComponent(newSelected)
        const newValues = set(screenValues, selectedComponent.path + '.styles', template);
        setScreenValues(newValues)
    }
    const toggleOpenTemplates = () => {
        setOpenSelectTemplate(prev => !prev);
    }

    //component actions
    const onDeleteComponent = (path, type, parentLength, parentPath) => {
        let newValues;
        let isListObject = path.split('.')[0] === 'listObject';
        if (isListObject) {
            let newPath = path.split('.').slice(1).join('.');
            let newLists = del(preparedToSaveLists, newPath);
            newLists[newPath.split('.')[0]].touched = true;
            setPreparedToSaveLists(newLists);
            return
        }

        //when removing last component from container, remove his parent
        if (parentLength === 1) {
            if (parentPath) {
                newValues = del(screenValues, parentPath);
            }
        } else {
            newValues = del(screenValues, path);
        }

        //check if it list, to remove from listStorage
        if (type === 'list') {
            const currentListName = selectedComponent.component ? selectedComponent.component.properties.name : '';
            let newListStorage = listStorage.filter(list => list !== currentListName);
            setListStorage(newListStorage || []);
        }

        //remove from selected
        if (selectedComponent && selectedComponent.path === path) {
            setSelectedComponent({component: {}, path: ""})
        }
        setScreenValues(newValues);
        captureMiddlewareState(newValues)
    };
    const onDuplicateComponent = (parentPath, item, itemIndex) => {
        if (selectedComponent && selectedComponent.isListObject) return;

        const newItem = item.children ? {...item, children: regenerateIds(item)} : item;

        const newValues = onDrop(screenValues, setLists, {
            ...newItem,
            id: generateUid(item),
        }, itemIndex + 1, parentPath);
        setScreenValues(newValues);
        captureMiddlewareState(newValues)
    };

    const onSelectComponent = (component, path, ref) => {
        const isListObject = path.split('.')[0] === 'listObject';

        //TODO DELETE IT AFTER FINISH SCREEN ADAPTIVE CHANGES
        let newTmpComponent = JSON.parse(JSON.stringify(component));
        if (component.styles && component.styles[0]) {
            if (!component.styles[0].mobile) {
                newTmpComponent.styles[0].mobile = {...component.styles[0], mobile: null}
            }
            if (!component.styles[0].tablet) {
                newTmpComponent.styles[0].tablet = {...component.styles[0], tablet: null}
            }
        }

        if (ref && ref.current) {
            selectedComponentRef.current = ref.current;
        }

        setSelectedComponent({component: newTmpComponent, path, isListObject})

    };


    //DND events
    const onDropComponent = (item, direction, path) => {
        let isListObject = path.split('.')[0] === 'listObject';
        item.type === 'sidebar' && dispatch(insertSideBar());
        if (isListObject) {
            const newItem = {id: generateUid(item), ...item}
            const newPath = path.split('.').slice(1, path.split('.').length - 1).join('.');
            const newListValues = insert(preparedToSaveLists, newPath, newItem, direction);
            newListValues[path.split('.')[1]].touched = true;
            setPreparedToSaveLists(newListValues);
            return
        }
        const newValues = onDrop(screenValues, setLists, {
            ...item,
            id: generateUid(item)
        }, direction, path);
        setScreenValues(newValues);
        captureMiddlewareState(newValues)
    };

    /**
     * @param {Object} item moving item.
     * @param {string} from path moves from
     * @param {string} to path moves to
     * @param {number} index of dropped area
     * @param {number} fromIndex draggable item index
     * @param {number} toIndex drop area index
     */
    const onMoveComponent = (item, from, to, index, fromIndex, toIndex) => {
        let isListObjectFrom = from.split('.')[0] === 'listObject';
        let isListObjectTo = to.split('.')[0] === 'listObject';

        const partsFrom = from.split('.');
        const partsTo = to.split('.');
        const smallerPath = partsFrom < partsTo ? partsFrom : partsTo;

        const indexFrom = partsFrom[smallerPath.length - 1];
        const indexTo = partsTo[smallerPath.length - 1];

        //MOVE INSIDE OR BETWEEN LISTS HANDLER
        if (isListObjectFrom || isListObjectTo) {
            let newValues;
            //DEFINE LIST NAMES
            let listNameFrom;
            let listNameTo
            if(isListObjectFrom) listNameFrom = partsFrom[1];
            if(isListObjectTo) listNameTo = partsTo[1];



            let newPathTo = isListObjectTo ?
                //generate path for list
                to.split('.').slice(1, to.split('.').length - 1).join('.') :
                //screen path
                to;
            let newPathFrom = isListObjectFrom ?
                //generate path for list
                from.split('.').slice(1, from.split('.').length).join('.') :
                //screen path
                from;

            //MOVING BETWEEN LISTS
            if(isListObjectFrom && isListObjectTo) {
                let ins = insert(preparedToSaveLists, newPathTo, item, index);
                let deleteFr = del(ins, newPathFrom)
                prepareToSaveLists({...ins[listNameTo], touched: true}, listNameTo)
                prepareToSaveLists({...deleteFr[listNameFrom || listNameTo], touched: true}, listNameFrom || listNameTo)
                return
            }

            //MOVING INSIDE LIST
            if (indexFrom < indexTo) {
                let ins = insert(isListObjectTo ? preparedToSaveLists : screenValues, newPathTo, item, index);
                setPreparedToSaveLists(ins)

                prepareToSaveLists({...ins[listNameTo], touched: true}, listNameTo)
                if(!isListObjectFrom) {
                    let removeDragged = del(screenValues, from)
                    setScreenValues(removeDragged)
                }
            }

            //MOVING FROM LIST TO SCREEN
            if(indexFrom > indexTo) {
                let remove = del(isListObjectFrom ? preparedToSaveLists : screenValues, newPathFrom);
                prepareToSaveLists({...remove[listNameFrom], touched: true}, listNameFrom)

                if(!isListObjectTo) {
                    newValues = insert(screenValues, newPathTo, item, index);
                    setScreenValues(newValues)
                }
            }
            return;
        }


        let newValues;
        if (indexFrom < indexTo) {
            let ins = insert(screenValues, to, item, index);
            newValues = del(ins, from);
            setScreenValues(newValues)
        } else {
            let remove = del(screenValues, from);
            newValues = insert(remove, to, item, index);
            setScreenValues(newValues)
        }
        captureMiddlewareState(newValues)
    };
    const onDropMultipleComponents = (items = [], direction, path) => {
        let copyScreenValues = JSON.parse(JSON.stringify(screenValues));
        const entity = items._entity;
        if (items._entity) {
            items = items.fields
        }
        if (entity) {
            const findEntity = copyScreenValues.otherComponents?.find(f => f.properties?.name === entity)
            if (!findEntity) {
                copyScreenValues = insert(copyScreenValues, 'otherComponents', {
                    type: 'entity',
                    properties: {name: entity}
                });
            }
        }
        for (let i = 0; i < items.length; i++) {
            copyScreenValues = insert(copyScreenValues, path, items[i], direction + i);
        }
        setScreenValues(copyScreenValues)
    };
    const dropInEveryChild = (item, path) => {
        const parent = findByString(screenValues, path);
        console.log(parent, path)
        const newParent = parent && parent.children.map((v, i) => {
            if(i === 0) {
                return {...v, children: [...v.children, {
                        type: 'tableTh',
                        styles: [{}],
                        children: []
                    }]}
            }
            return {...v, children: [...v.children, item]}
        })
        const newScreenValues = set(screenValues, `${path}.children`, newParent)
        setScreenValues(newScreenValues)
    }

    //change styles components
    const changeWidthByDrag = (path, newWidth) => {
        const newValues = set(screenValues, `${path}.width`, newWidth);
        setScreenValues(newValues)
    };


    //undo redo logic
    const captureMiddlewareState = (newValues) => {
        if (middleWareValues.length > 4) {
            //shift one and push new
            let shiftedArr = middleWareValues.slice(1);
            setMiddleWareValues([...shiftedArr, newValues]);
            setCurrentStep(5);
            return
        }
        //push new
        setMiddleWareValues(prevState => [...prevState, newValues]);
        //add step
        setCurrentStep(middleWareValues.length)

    };
    const undoAction = () => {
        //  console.log(currentStep, middleWareValues)
        // if (currentStep >= 1) {
        //     setScreenValues(middleWareValues[currentStep - 1]);
        //     setCurrentStep(prevState => prevState - 1)
        // }
    };
    const redoAction = () => {
        //    console.log(currentStep, middleWareValues)
        // if (currentStep >= 0 && currentStep + 1 < middleWareValues.length) {
        //     setScreenValues(middleWareValues[currentStep + 1]);
        //     if (currentStep !== middleWareValues.length) {
        //         setCurrentStep(prevState => prevState + 1)
        //     }
        // }
    };


    //screen resolutions
    const setScreenResolution = (value) => {
        setResolution({resolution: {...value}})
    };

    //field handler
    const onChangeDefaultFields = (value, path) => {
        if (selectedComponent && selectedComponent.isListObject) {
            const newValues = set(preparedToSaveLists, path, value);
            newValues[path.split('.')[0]].touched = true;
            return setPreparedToSaveLists(prevState => ({
                ...prevState,
                ...newValues
            }));
        }
        const newValues = set(screenValues, path, value);
        setScreenValues(newValues)
        if (selectedComponent && selectedComponent.path) {
            const newSelected = findByString(newValues, selectedComponent.path);
            setSelectedComponent(prev => {
                return {
                    ...prev,
                    component: newSelected
                }
            })
        }

    };

    //setListStorage
    const addNewList = (newList) => {
        const isExists = listStorage.find(list => list === newList);
        !isExists && setListStorage(prevState => [...prevState, newList])
    };

    //events and actions handlers change
    const onChangeEventComponent = (value, path) => {
        const screenValuesPath = selectedComponent.path ? `${selectedComponent.path}.events.${path}` : `events.${path}`;
        if (selectedComponent.isListObject) {
            const newPath = selectedComponent.path.split('.').slice(1).join('.');
            const newValues = set(preparedToSaveLists, `${newPath}.events.${path}`, value);
            newValues[newPath.split('.')[0]].touched = true;
            setPreparedToSaveLists(prevState => ({
                ...prevState,
                ...newValues
            }))
        } else {
            const newScreenValues = set(screenValues, screenValuesPath, value);
            setScreenValues(newScreenValues);
        }
        const newSelectedComponent = set(selectedComponent.component, `events.${path}`, value);
        setSelectedComponent(prevValues => ({
            ...prevValues,
            component: newSelectedComponent
        }))
    };
    const actionSave = (eventIndex, actions) => {
        let actionsToSave = actions;
        let scriptToSave = screenValues.script;
        if (screenValues.version === 3) {
            actions.forEach((action, i) => {
                if (action.type === 'executeJs') {
                    const {value} = action;
                    const {id, type: componentType, events, properties: {path} = {}} = selectedComponent.component;
                    const eventType = events[eventIndex].event;
                    const fnName = componentType === 'field' ?
                        `${path.split('.').join('_')}_${id}_${eventType}` :
                        componentType === 'container' ? `${screenValues.id}_${eventType}` : `${id}_${eventType}`;
                    const valueJs = getScriptFromBraces(value)
                    const newScript = `
//->${fnName}
async function ${fnName}() {
${valueJs}
}
//<-${fnName}
`
                    //find in main script is existing current
                    let regex = new RegExp(String.raw`(async function ${fnName}\(\)\s*{)([\s\S]*?)(})`);
                    const match = screenValues.script?.match(regex);
                    if (!valueJs) {
                        let regex = new RegExp(`//->${fnName}[\\s\\S]*?//<-${fnName}`, "g");
                        if (match) {
                            scriptToSave = screenValues.script.replace(regex, "")
                            actionsToSave[i] = {...action, value: ``}
                        }
                    } else {
                        let regToUpdate = new RegExp(String.raw`(async function ${fnName}\(\) \{\n)[\s\S]*?(\n})`);
                        let updatedStr = screenValues.script?.replace(regToUpdate, `$1\n    ${valueJs}\n$2`);
                        scriptToSave = match ? updatedStr : screenValues.script ? screenValues.script + '\n' + newScript : newScript;
                        actionsToSave[i] = {...action, value: `{${fnName}()}`}
                    }


                }
            })
        }
        const screenValuesPath = selectedComponent.path ? `${selectedComponent.path}.events.${eventIndex}.actions` : `events.${eventIndex}.actions`;
        if (selectedComponent.isListObject) {
            const newPath = selectedComponent.path.split('.').slice(1).join('.');
            const newValues = set(preparedToSaveLists, `${newPath}.events.${eventIndex}.actions`, actionsToSave);
            newValues[newPath.split('.')[0]].touched = true;
            setPreparedToSaveLists(prevState => ({
                ...prevState,
                ...newValues
            }))
            setScreenValues(prev => {
                return {
                    ...prev,
                    script: scriptToSave,
                }
            });
        } else {
            const newScreenValues = set(screenValues, screenValuesPath, actionsToSave);
            setScreenValues(prev => {
                return {
                    ...newScreenValues,
                    script: scriptToSave,
                }
            });
        }
        const newSelectedComponent = set(selectedComponent.component, `events.${eventIndex}.actions`, actionsToSave);
        setSelectedComponent(prevValues => ({...prevValues, component: newSelectedComponent}))
    };
    const createEvent = (index) => {
        const screenValuesPath = selectedComponent.path ? `${selectedComponent.path}.events` : `events`;
        const emptyEvent = {event: "", actions: [{param: [{}]}]};

        if (selectedComponent.isListObject) {
            const newPath = selectedComponent.path.split('.').slice(1).join('.');
            const newValues = insert(preparedToSaveLists, `${newPath}.events`, emptyEvent, index);
            newValues[newPath.split('.')[0]].touched = true;
            setPreparedToSaveLists(prevState => ({
                ...prevState,
                ...newValues
            }))
        } else {
            const newScreenValues = insert(screenValues, screenValuesPath, emptyEvent, index);
            setScreenValues(newScreenValues);
        }
        const newSelectedComponent = insert(selectedComponent.component, `events`, emptyEvent, index);
        setSelectedComponent(prevValues => ({
            ...prevValues,
            component: newSelectedComponent
        }))
    };
    const removeEvent = (index) => {
        const currentEvents = findByString(selectedComponent.component, 'events');
        let newScreenValues, newSelectedComponent;
        const emptyEvent = {event: "", actions: [{param: [{}]}]};
        if (selectedComponent.isListObject) {
            const newPath = selectedComponent.path.split('.').slice(1).join('.');
            if (currentEvents.length === 0 || currentEvents.length === 1) {
                newScreenValues = set(preparedToSaveLists, `${newPath}.events.0`, emptyEvent);
                newSelectedComponent = set(selectedComponent.component, `events.0`, emptyEvent);
                newScreenValues[newPath.split('.')[0]].touched = true;
                setPreparedToSaveLists(prevState => ({
                    ...prevState,
                    ...newScreenValues
                }));
                setSelectedComponent(prevValues => ({
                    ...prevValues,
                    component: newSelectedComponent
                }));
            } else {
                newScreenValues = del(preparedToSaveLists, `${newPath}.events.${index}`);
                newScreenValues[newPath.split('.')[0]].touched = true;
                newSelectedComponent = del(selectedComponent.component, `events.${index}`);
                setPreparedToSaveLists(prevState => ({
                    ...prevState,
                    ...newScreenValues
                }));
                setSelectedComponent(prevValues => ({
                    ...prevValues,
                    component: newSelectedComponent
                }));
            }
            return
        }

        if (currentEvents.length === 0 || currentEvents.length === 1) {
            const screenValuesPath = selectedComponent.path ? `${selectedComponent.path}.events.0` : `events.0`;
            newScreenValues = set(screenValues, screenValuesPath, emptyEvent);
            newSelectedComponent = set(selectedComponent.component, `events.0`, emptyEvent);
        } else {
            const screenValuesPath = selectedComponent.path ? `${selectedComponent.path}.events.${index}` : `events.${index}`;
            newScreenValues = del(screenValues, screenValuesPath);
            newSelectedComponent = del(selectedComponent.component, `events.${index}`);
        }
        setScreenValues(newScreenValues);
        setSelectedComponent(prevValues => ({
            ...prevValues,
            component: newSelectedComponent
        }));
    };


    const toggleListDesigner = (listId) => {
        setOpenListDesigner(prevState => !prevState.open ? {listId, open: true} : {listId: "", open: false})
    };
    const setImageContent = ({name, base64, pathToProps}) => {
        const imageObject = {
            content: base64,
            name: name,
            type: "screenImage",
            refEntity: "screen",
            refId: screenValues.id,
            path: selectedComponent.path + pathToProps
        };
        const imageObjectShow = {content: base64, name: name};
        let findSame = _.findIndex(preparedToSaveImages, (o) => {
            return o.path === selectedComponent.path
        });
        let uplIndex = findSame !== -1 ? findSame : preparedToSaveImages.length;
        const newPreparedToSaveImages = set(preparedToSaveImages, uplIndex, imageObject);

        //upload images to show them on the screen
        let newUploadedImages = set(uploadedImages, `${selectedComponent.path}` + pathToProps, imageObjectShow);
        setUploadedImages(newUploadedImages);
        setPreparedToSaveImages(newPreparedToSaveImages)
    };
    const setListData = (schema, name) => {
        setLists(prevState => {
            return {
                ...prevState,
                [name]: {columns: schema.columns, filterFields: schema.filterFields, name}
            }
        });
    };
    const prepareToSaveLists = (schema, name) => {
        setPreparedToSaveLists(prevProps => ({
            ...prevProps,
            [name]: schema
        }))
    };


    return {
        screenValues,
        currentCustomResolution: screenValues.resolution ? screenValues.resolution.id : 'desktop',
        isDesignerReady,
        screenTypeProps,
        resolution,
        setScreenResolution,
        lists,
        listStorage,
        openListDesigner,
        toggleListDesigner,
        selectedComponent,
        selectedComponentRef,
        onSelectStyleTemplate,
        suggestions,
        uploadedImages,
        setImageContent,
        setListData,
        setSelectedComponent,
        isChanged,
        loading,
        loadInitialData,
        initData,
        scriptSuggestions,
        preparedToSaveLists,
        saveScreen,
        removeScreen,
        closeScreen,
        toggleOpenTemplates,
        onSelectTemplate,
        openSelectTemplate,
        onDropComponent,
        dropInEveryChild,
        onDropMultipleComponents,
        onDeleteComponent,
        onSelectComponent,
        onMoveComponent,
        onDuplicateComponent,
        onChangeDefaultFields,
        prepareToSaveLists,
        //event and actions handlers
        onChangeEventComponent,
        actionSave,
        createEvent,
        removeEvent,
        addNewList
    }
};

export default useScreenDesigner